<!-- components/atlas/AtlasCategoryView.vue -->
<template>
    <div class="category-view">
        <!-- Хлебные крошки -->
        <nav class="breadcrumbs mb-4">
            <ul class="flex items-center space-x-2">
                <li>
                    <router-link to="/atlas" class="text-gray-600 hover:text-primary">
                        Гематологический атлас
                    </router-link>
                </li>
                <li v-for="crumb in breadcrumbs" :key="crumb._id" class="flex items-center">
                    <i class="pi pi-angle-right mx-2 mt-1 text-gray-400"></i>
                    <router-link 
                        :to="{ name: 'AtlasCategoryView', params: { id: crumb._id }}"
                        class="text-gray-600 hover:text-primary"
                    >
                        {{ crumb.name }}
                    </router-link>
                </li>
            </ul>
        </nav>

        <div v-if="loading" class="flex justify-center items-center p-6">
            <ProgressSpinner />
        </div>
        <div v-else-if="error" class="text-red-500 p-4 bg-red-50 rounded">
            {{ error }}
        </div>
        <div v-else>
            <h1 class="text-2xl font-bold mb-6">{{ category.name }}</h1>
            
            <!-- Описание категории -->
            <p v-if="category.description" class="text-gray-600 mb-6">
                {{ category.description }}
            </p>

            <!-- Подкатегории -->
            <div v-if="subcategories.length" class="mb-8">
                <h2 class="text-xl font-semibold mb-4">Подкатегории</h2>
                <div class="viewers-list flex flex-wrap gap-3">
                    <div 
                        v-for="subcat in subcategories" 
                        :key="subcat._id"
                        class="document"
                        @mouseover="hover(subcat._id)" 
                        @mouseleave="leave"
                    >
                        <router-link 
                            :to="subcat.images_count ? {name: 'AtlasCategoryView', params: {id: subcat._id}} : ''"
                            class="text-decoration-none"
                            :class="{'disabled-link': !subcat.images_count}"
                        >
                            <div v-if="hovered !== subcat._id">
                                <div class="document-icon-wrapper d-inline-flex justify-content-center align-items-center mb-2 w-full">
                                    <div class="icon">
                                        <img 
                                            v-if="subcat.thumbnail_url" 
                                            :src="getImageUrl(subcat.thumbnail_url)" 
                                            :alt="subcat.name"
                                            class="category-thumbnail"
                                        >
                                        <i v-else class="pi pi-image"></i>
                                    </div>
                                </div>
                                <div class="document-name text-center">
                                    {{ subcat.name }}
                                    <div class="image-counter" v-if="subcat.images_count">
                                        {{ subcat.images_count }} фото
                                    </div>
                                </div>
                            </div>
                            <div v-else>
                                <div class="open-button" v-if="subcat.images_count">Просмотреть</div>
                                <div class="open-button disabled" v-else>Нет фото</div>
                            </div>
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Фотогалерея -->
            <div v-if="photos.length" class="photos-section">
                <h2 class="text-xl font-semibold mb-4">Фотографии</h2>
                <div class="photo-grid">
                    <div 
                        v-for="photo in photos" 
                        :key="photo._id"
                        class="photo-item cursor-pointer"
                        @click="openViewer(photo)"
                    >
                        <div class="photo-wrapper">
                            <img 
                                :src="getImageUrl(photo.thumbnail_url)"
                                :alt="photo.description || 'Медицинское изображение'"
                                class="photo-image"
                            >
                            <div class="photo-overlay">
                                <span class="photo-description" v-if="photo.description">
                                    {{ photo.description }}
                                </span>
                                <i class="pi pi-search-plus overlay-icon"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- OpenSeadragon Viewer -->
            <Dialog 
                v-model:visible="showViewer" 
                :modal="true"
                :dismissableMask="true"
                :closeOnEscape="true"
                class="viewer-dialog"
                maximizable
            >
                <template #header>
                    <div class="flex justify-between items-center w-full">
                        <span>{{ selectedPhoto?.description || 'Просмотр изображения' }}</span>
                        <div class="flex items-center space-x-2">
                            <Button 
                                icon="pi pi-download" 
                                @click="downloadImage"
                                class="p-button-text"
                                v-tooltip.top="'Скачать оригинал'"
                            />
                        </div>
                    </div>
                </template>
                <div id="openseadragon-viewer" class="w-full" style="height: 80vh;"></div>
            </Dialog>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import http from '@/http'
import OpenSeadragon from 'openseadragon'
import ProgressSpinner from 'primevue/progressspinner'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'

const route = useRoute()
const loading = ref(true)
const error = ref(null)
const category = ref(null)
const subcategories = ref([])
const photos = ref([])
const breadcrumbs = ref([])
const showViewer = ref(false)
const selectedPhoto = ref(null)
const viewer = ref(null)
const hovered = ref(null)

const fetchCategoryData = async () => {
    try {
        loading.value = true
        const [categoryResponse, subcatsResponse, photosResponse, breadcrumbsResponse] = await Promise.all([
            http.get(`/atlas/categories/${route.params.id}`),
            http.get(`/atlas/categories/${route.params.id}/children`),
            http.get(`/atlas/categories/${route.params.id}/photos`),
            http.get(`/atlas/categories/${route.params.id}/breadcrumbs`)
        ])

        category.value = categoryResponse.data
        subcategories.value = subcatsResponse.data
        photos.value = photosResponse.data
        breadcrumbs.value = breadcrumbsResponse.data
    } catch (err) {
        console.error('Error fetching category data:', err)
        error.value = 'Ошибка при загрузке данных категории'
    } finally {
        loading.value = false
    }
}

const initViewer = () => {
    if (!viewer.value) {
        viewer.value = OpenSeadragon({
            id: 'openseadragon-viewer',
            prefixUrl: "/images/",
            animationTime: 0.5,
            blendTime: 0.1,
            constrainDuringPan: true,
            maxZoomPixelRatio: 2,
            minZoomLevel: 1,
            visibilityRatio: 1,
            zoomPerScroll: 2
        })
    }
    
    if (selectedPhoto.value) {
        viewer.value.open({
            type: 'image',
            url: getImageUrl(selectedPhoto.value.image_url)
        })
    }
}

const openViewer = (photo) => {
    selectedPhoto.value = photo
    showViewer.value = true
}

const downloadImage = async () => {
    if (!selectedPhoto.value) return
    
    const link = document.createElement('a')
    link.href = getImageUrl(selectedPhoto.value.image_url)
    link.download = `image_${selectedPhoto.value._id}.jpg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const getImageUrl = (path) => {
    return path ? `https://api.labhub.online${path}` : null
}

const hover = (id) => {
    hovered.value = id
}

const leave = () => {
    hovered.value = null
}

watch(() => route.params.id, () => {
    fetchCategoryData()
})

watch(showViewer, (newVal) => {
    if (newVal) {
        setTimeout(() => {
            initViewer()
        }, 100)
    } else {
        if (viewer.value) {
            viewer.value.destroy()
            viewer.value = null
        }
    }
})

onMounted(() => {
    fetchCategoryData()
})
</script>


<style scoped>

.breadcrumbs ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.viewers-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 20px;
}

.document {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 300px;
    height: 200px;
    padding: 20px;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
}

.document:hover {
    background-color: #e8f8ff;
    transform: translateY(-2px);
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
}

.document-icon-wrapper {
    transition: opacity 0.3s ease;
}

.document-icon-wrapper .line {
    width: 35px;
    height: 1px;
    background: rgba(39, 39, 85, 0.1);
    border-radius: 100px;
    border: 1px solid rgba(39, 39, 85, 0.1);
}

.category-thumbnail {
    width: 100%;
    height: 120px;
    object-fit: contain;
}

.document-name {
    color: #5f5f5f;
    font-family: Nunito;
    font-size: 14px;
    font-weight: 500;
    margin-top: 15px;
    transition: opacity 0.3s ease;
    text-align: center;
}

.image-counter {
    display: inline-block;
    background: var(--primary-light, #e3f2fd);
    color: var(--primary, #1384c5);
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    margin-top: 4px;
}

.view-link {
    display: inline-flex;
    align-items: center;
    padding: 6px 16px;
    background: #3fe7de;
    color: white;
    border-radius: 4px;
    text-decoration: none;
    font-weight: 500;
    transition: background-color 0.2s;
}

.view-link:hover {
    background: var(--primary-dark, #0d5d8f);
}

.open-button {
    background: #3fe7de;
    color: white;
    padding: 8px 20px;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.open-button:hover {
    background: #20b1aa;
}

.open-button.disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.disabled-link {
    pointer-events: none;
    opacity: 0.6;
}

.photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
    padding: 1rem;
}

.photo-item {
    position: relative;
    width: 100%;
}

.photo-wrapper {
    position: relative;
    padding-top: 100%;
    background: #f8f9fa;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.photo-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 4px;
    transition: transform 0.3s ease;
}

.photo-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    color: white;
    padding: 1rem;
}

.photo-item:hover .photo-overlay {
    opacity: 1;
}

.photo-description {
    text-align: center;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
    max-height: 60%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
}

.overlay-icon {
    font-size: 1.5rem;
    color: white;
}

:deep(.viewer-dialog) {
    width: 90vw !important;
    max-width: 1200px !important;
}

:deep(.p-dialog-header) {
    padding: 1rem !important;
}

:deep(.p-dialog-content) {
    padding: 0 !important;
}

@media (min-width: 1200px) {
    .document {
        flex: 1 1 calc(33.333% - 20px);
    }
}

@media (min-width: 768px) and (max-width: 1199px) {
    .document {
        flex: 1 1 calc(50% - 20px);
    }
}

@media (max-width: 767px) {
    .document {
        flex: 1 1 100%;
    }
    
    .photo-grid {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        gap: 0.5rem;
        padding: 0.5rem;
    }
}
</style>